import {
  findInGraph,
  graphIds,
  normalizeDefinition,
  type RequestContextValues,
  type WorkflowDefinition,
  type WorkflowDto,
  type WorkflowEventDto,
  type WorkflowInput,
  type WorkflowRunDto,
} from '@agent-platform/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Ban,
  Clock,
  Eye,
  FastForward,
  Hand,
  History,
  Play,
  RotateCcw,
  SkipForward,
  Square,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ErrorAlert } from '@/components/error-alert';
import { RequestContextPicker } from '@/components/request-context-picker';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { STATUS_META } from '@/components/workflow-graph';
import { api, ApiError, errorMessage } from '@/lib/api';
import { formatDateTime, formatDuration } from '@/lib/format';
import { getWorkflowStream, postWorkflowStream } from '@/lib/sse';
import { cn } from '@/lib/utils';
import {
  applyEvent,
  ENTRY_META,
  formatValue,
  fromResults,
  WORKFLOW_STATUS_LABEL,
  WORKFLOW_STATUS_VARIANT,
  type LiveStep,
} from '@/lib/workflow-ui';

type Completed = Extract<WorkflowEventDto, { type: 'workflow.completed' }>;

/** Inputs for a set of declared fields (run input, human step form). */
function FieldInputs({
  fields,
  values,
  prefix,
  error,
  onChange,
}: {
  fields: WorkflowInput[];
  values: Record<string, string>;
  prefix: string;
  error: unknown;
  onChange: (values: Record<string, string>) => void;
}) {
  const fieldError = (name: string) =>
    error instanceof ApiError ? error.fieldError(`${prefix}.${name}`) : undefined;
  return (
    <>
      {fields.map((field) => {
        const id = `wf-${prefix}-${field.name}`;
        const value = values[field.name] ?? '';
        const set = (v: string) => onChange({ ...values, [field.name]: v });
        return (
          <div key={field.name} className="grid gap-1.5">
            <Label htmlFor={id} className="font-mono text-xs">
              {field.name}
              {field.required && <span aria-hidden> *</span>}{' '}
              <span className="text-muted-foreground font-sans">{field.type}</span>
            </Label>
            {field.type === 'boolean' ? (
              <div className="flex gap-2" role="radiogroup" aria-label={field.name}>
                {['true', 'false'].map((v) => (
                  <Button
                    key={v}
                    type="button"
                    size="sm"
                    variant={value === v ? 'default' : 'outline'}
                    aria-pressed={value === v}
                    onClick={() => set(v)}
                  >
                    {v === 'true' ? 'Yes' : 'No'}
                  </Button>
                ))}
              </div>
            ) : field.type === 'json' ? (
              <Textarea
                id={id}
                className="font-mono text-xs"
                value={value}
                placeholder={field.description}
                onChange={(e) => set(e.target.value)}
              />
            ) : (
              <Input
                id={id}
                value={value}
                inputMode={field.type === 'number' ? 'decimal' : undefined}
                placeholder={field.description}
                onChange={(e) => set(e.target.value)}
              />
            )}
            {field.description && field.type === 'boolean' && (
              <p className="text-muted-foreground text-xs">{field.description}</p>
            )}
            {fieldError(field.name) && (
              <p className="text-destructive text-xs">{fieldError(field.name)}</p>
            )}
          </div>
        );
      })}
    </>
  );
}

/**
 * Starts runs and drives them (Mastra Studio's run panel): live step results, step-by-step mode,
 * human input, cancel, watching background runs, and time travel from any step.
 */
export function WorkflowRunPanel({
  tenantId,
  workflow,
  draft,
  selected,
  loadRunId,
  onLive,
  onRun,
}: {
  tenantId: string;
  workflow: WorkflowDto;
  /** Unsaved changes in the editor: runs use them instead of the saved workflow. */
  draft: WorkflowDefinition | null;
  /** The node selected in the graph (time travel starts there). */
  selected: string | null;
  /** A run picked in the history to show (and continue). */
  loadRunId: string | null;
  onLive: (live: Record<string, LiveStep>) => void;
  onRun: (run: WorkflowRunDto | null) => void;
}) {
  const queryClient = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  const [context, setContext] = useState<RequestContextValues | null>({});
  const [debug, setDebug] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [run, setRun] = useState<WorkflowRunDto | null>(null);
  const [live, setLive] = useState<Record<string, LiveStep>>({});
  const [result, setResult] = useState<Completed | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [resumeValues, setResumeValues] = useState<Record<string, string>>({});
  const [travelInput, setTravelInput] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => () => abortRef.current?.abort(), []);
  /** What a new run runs: the editor's draft, else the saved workflow. */
  const current = draft ?? workflow.definition;
  useEffect(() => {
    onLive(live);
  }, [live, onLive]);
  useEffect(() => {
    onRun(run);
  }, [run, onRun]);

  const refresh = useCallback(
    async (runId: string) => {
      const fresh = await api<WorkflowRunDto>(`/workflow-runs/${runId}`);
      setRun(fresh);
      setLive(fromResults(fresh.steps));
      void queryClient.invalidateQueries({ queryKey: ['workflow-runs', tenantId, workflow.id] });
      void queryClient.invalidateQueries({ queryKey: ['workflows', tenantId] });
      void queryClient.invalidateQueries({ queryKey: ['workflow-run-counts', tenantId] });
      return fresh;
    },
    [queryClient, tenantId, workflow.id],
  );

  /** Streams one request's events into the panel, then reloads the run. */
  const stream = useCallback(
    async (
      open: (signal: AbortSignal, onEvent: (e: WorkflowEventDto) => void) => Promise<void>,
    ) => {
      abortRef.current?.abort();
      const abort = new AbortController();
      abortRef.current = abort;
      setStreaming(true);
      setError(null);
      setResult(null);
      let runId: string | null = null;
      try {
        await open(abort.signal, (event) => {
          if (event.type === 'workflow.started') runId = event.runId;
          if (event.type === 'workflow.completed') {
            runId = event.runId;
            setResult(event);
          }
          setLive((all) => applyEvent(all, event));
        });
        if (runId) await refresh(runId);
      } catch (e) {
        setError(e);
      } finally {
        if (abortRef.current === abort) abortRef.current = null;
        setStreaming(false);
      }
    },
    [refresh],
  );

  // Load a run picked in the history (and watch it if it is still going).
  useEffect(() => {
    if (!loadRunId) return;
    let cancelled = false;
    void api<WorkflowRunDto>(`/workflow-runs/${loadRunId}`)
      .then((r) => {
        if (cancelled) return;
        setRun(r);
        setLive(fromResults(r.steps));
        setResult(null);
        setTravelInput(null);
        if (r.status === 'running')
          void stream((signal, onEvent) =>
            getWorkflowStream(`/workflow-runs/${r.id}/observe`, onEvent, signal),
          );
      })
      .catch((e: unknown) => setError(e));
    return () => {
      cancelled = true;
    };
  }, [loadRunId, stream]);

  const start = () => {
    if (context === null) return;
    const def = current;
    setRun(null);
    setTravelInput(null);
    setLive(
      Object.fromEntries(graphIds(def.graph).map((id) => [id, { status: 'pending' as const }])),
    );
    void stream((signal, onEvent) =>
      postWorkflowStream(
        `/workflows/${workflow.id}/run`,
        { input: values, requestContext: context, debug, ...(draft ? { definition: draft } : {}) },
        onEvent,
        signal,
      ),
    );
  };

  const post = (path: string, body: unknown = {}) =>
    stream((signal, onEvent) => postWorkflowStream(path, body, onEvent, signal));

  const cancel = async () => {
    if (!run && !result) return;
    const id = run?.id ?? result!.runId;
    try {
      await api(`/workflow-runs/${id}/cancel`, { method: 'POST' });
      abortRef.current?.abort();
      await refresh(id);
      toast.success('Run cancelled');
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const status = run?.status ?? (streaming ? 'running' : null);
  const def = run ? normalizeDefinition(run.definition) : current;
  const suspended =
    run?.status === 'suspended' && run.suspendedStepId
      ? findInGraph(def.graph, run.suspendedStepId)
      : null;
  const human = suspended?.step.type === 'human' ? suspended.step : null;
  const target = run && selected ? findInGraph(def.graph, selected) : null;
  const canTravel = Boolean(run && target && !streaming && run.status !== 'running');
  const failed = run?.steps.find((s) => s.status === 'failed');
  const ids = graphIds(def.graph);

  return (
    <div className="grid gap-4">
      {draft && (
        <p className="text-muted-foreground rounded-md border border-dashed p-2 text-xs">
          Runs use your unsaved changes. They are not saved until you click Save.
        </p>
      )}
      {!run || ['succeeded', 'failed', 'cancelled'].includes(run.status) ? (
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            start();
          }}
        >
          {current.inputs.length === 0 && (
            <p className="text-muted-foreground text-sm">This workflow takes no input.</p>
          )}
          <FieldInputs
            fields={current.inputs}
            values={values}
            prefix="input"
            error={error}
            onChange={setValues}
          />
          <RequestContextPicker tenantId={tenantId} id="wf-run-context" onChange={setContext} />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={debug} onChange={(e) => setDebug(e.target.checked)} />
            Step by step (pause after each step)
          </label>
          <Button type="submit" disabled={context === null || streaming}>
            <Play aria-hidden /> {run ? 'Run again' : 'Run workflow'}
          </Button>
        </form>
      ) : null}

      <ErrorAlert error={error} />

      {status && (
        <div className="grid gap-2 rounded-md border p-3" data-testid="workflow-run-status">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Badge variant={WORKFLOW_STATUS_VARIANT[status]}>{WORKFLOW_STATUS_LABEL[status]}</Badge>
            {run && (
              <span className="text-muted-foreground text-xs">
                {formatDateTime(run.startedAt)}
                {run.latencyMs !== null && ` · ${formatDuration(run.latencyMs)}`}
                {run.trigger !== 'console' && ` · by ${run.trigger}`}
              </span>
            )}
            {run && (
              <Link
                to="/traces/workflows/$runId"
                params={{ runId: run.id }}
                className="text-muted-foreground ml-auto text-xs underline"
              >
                Trace
              </Link>
            )}
          </div>
          {run?.timeTravel && (
            <p className="text-muted-foreground flex items-center gap-1 text-xs">
              <History className="size-3" aria-hidden /> Re-ran from{' '}
              <span className="font-mono">{run.timeTravel.stepId}</span> of{' '}
              <Link
                to="/traces/workflows/$runId"
                params={{ runId: run.timeTravel.fromRunId }}
                className="underline"
              >
                an earlier run
              </Link>
            </p>
          )}
          {(run?.error || result?.error) && run?.status !== 'cancelled' && (
            <p className="text-destructive text-xs">{run?.error ?? result?.error}</p>
          )}

          <div className="flex flex-wrap gap-2">
            {streaming && (
              <Button size="sm" variant="ghost" onClick={() => abortRef.current?.abort()}>
                <Square aria-hidden /> Stop watching
              </Button>
            )}
            {!streaming && run?.status === 'running' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  void stream((signal, onEvent) =>
                    getWorkflowStream(`/workflow-runs/${run.id}/observe`, onEvent, signal),
                  )
                }
              >
                <Eye aria-hidden /> Watch
              </Button>
            )}
            {run?.status === 'paused' && !streaming && (
              <>
                <Button size="sm" onClick={() => void post(`/workflow-runs/${run.id}/next`)}>
                  <SkipForward aria-hidden /> Run next step
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void post(`/workflow-runs/${run.id}/continue`)}
                >
                  <FastForward aria-hidden /> Continue
                </Button>
              </>
            )}
            {(streaming ||
              (run && ['running', 'paused', 'suspended', 'waiting'].includes(run.status))) && (
              <Button size="sm" variant="ghost" onClick={() => void cancel()}>
                <Ban aria-hidden /> Cancel run
              </Button>
            )}
            {run?.status === 'failed' && failed && !streaming && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  void post(`/workflow-runs/${run.id}/time-travel`, { stepId: failed.stepId })
                }
              >
                <RotateCcw aria-hidden /> Restart from {failed.name}
              </Button>
            )}
          </div>

          {run?.status === 'waiting' && run.resumeAt && (
            <p className="flex items-center gap-1.5 text-xs">
              <Clock className="size-3.5" aria-hidden /> Sleeping until{' '}
              {formatDateTime(run.resumeAt)}. It continues on its own.
            </p>
          )}

          {human && run && (
            <form
              className="grid gap-3 rounded-md border border-amber-500/50 p-3"
              aria-label="Respond to the workflow"
              onSubmit={(e) => {
                e.preventDefault();
                void post(`/workflow-runs/${run.id}/resume`, { data: resumeValues }).then(() =>
                  setResumeValues({}),
                );
              }}
            >
              <p className="flex items-center gap-1.5 text-sm font-medium">
                <Hand className="size-4 text-amber-600" aria-hidden /> {human.name}
              </p>
              <p className="text-sm whitespace-pre-wrap">
                {run.steps.find((s) => s.stepId === human.id)?.message ?? human.message}
              </p>
              <FieldInputs
                fields={human.fields}
                values={resumeValues}
                prefix="data"
                error={error}
                onChange={setResumeValues}
              />
              <Button type="submit" size="sm" className="justify-self-start" disabled={streaming}>
                Submit and resume
              </Button>
            </form>
          )}
        </div>
      )}

      {canTravel && target && (
        <div className="grid gap-2 rounded-md border p-3" aria-label="Time travel">
          <p className="flex items-center gap-1.5 text-sm font-medium">
            <History className="size-4" aria-hidden /> Re-run from “{target.step.name}”
          </p>
          <p className="text-muted-foreground text-xs">
            A new run reuses this run’s results before the step and runs it and everything after it
            again.
          </p>
          {target.step.type !== 'human' && target.step.id === selected && (
            <>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={travelInput !== null}
                  onChange={(e) => {
                    const last = run!.steps.find((s) => s.stepId === selected)?.input;
                    setTravelInput(
                      e.target.checked ? (last === undefined ? '' : formatValue(last)) : null,
                    );
                  }}
                />
                Change this step’s input
              </label>
              {travelInput !== null && (
                <Textarea
                  aria-label="Step input"
                  className="min-h-24 font-mono text-xs"
                  value={travelInput}
                  onChange={(e) => setTravelInput(e.target.value)}
                />
              )}
            </>
          )}
          <Button
            size="sm"
            variant="outline"
            className="justify-self-start"
            onClick={() => {
              let stepInput: unknown;
              if (travelInput !== null) {
                try {
                  stepInput = /^\s*[[{]/.test(travelInput) ? JSON.parse(travelInput) : travelInput;
                } catch {
                  stepInput = travelInput;
                }
              }
              void post(`/workflow-runs/${run!.id}/time-travel`, {
                stepId: selected,
                ...(stepInput !== undefined ? { stepInput } : {}),
                debug,
              });
              setTravelInput(null);
            }}
          >
            <RotateCcw aria-hidden /> Re-run from here
          </Button>
        </div>
      )}

      {Object.keys(live).length > 0 && (
        <ol className="grid gap-2" aria-label="Step results">
          {ids.map((id) => {
            const step = live[id];
            const found = findInGraph(def.graph, id);
            if (!step || !found || step.status === 'pending') return null;
            const meta = STATUS_META[step.status];
            const nested = found.step.id !== found.entry.id;
            return (
              <li
                key={id}
                className={cn('grid gap-1 rounded-md border p-2 text-xs', nested && 'ml-4')}
                data-testid="workflow-step-result"
              >
                <div className="flex items-center gap-2">
                  <meta.icon className={cn('size-3.5', meta.className)} aria-hidden />
                  <span className="font-medium">{found.step.name}</span>
                  <span className="text-muted-foreground">
                    {ENTRY_META[found.step.type].label.toLowerCase()}
                  </span>
                  {step.iterations !== undefined && (
                    <span className="font-mono">×{step.iterations}</span>
                  )}
                  {step.items && (
                    <span className="font-mono">
                      {step.items.done}/{step.items.total}
                    </span>
                  )}
                  <span className="sr-only">{meta.label}</span>
                  {step.runId && (
                    <Link
                      to="/traces/runs/$runId"
                      params={{ runId: step.runId }}
                      className="text-muted-foreground ml-auto underline"
                    >
                      agent trace
                    </Link>
                  )}
                  {step.childRunId && (
                    <Link
                      to="/traces/workflows/$runId"
                      params={{ runId: step.childRunId }}
                      className="text-muted-foreground ml-auto underline"
                    >
                      nested run
                    </Link>
                  )}
                </div>
                {step.error && <p className="text-destructive">{step.error}</p>}
                {(step.output !== undefined && step.output !== null) || step.text ? (
                  <pre className="bg-muted max-h-40 overflow-auto rounded p-2 whitespace-pre-wrap">
                    {step.output !== undefined && step.output !== null
                      ? formatValue(step.output)
                      : step.text}
                  </pre>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}

      {(run?.status === 'succeeded' || result?.status === 'succeeded') && (
        <div className="grid gap-1 rounded-md border p-3 text-sm" data-testid="workflow-result">
          <span className="font-medium">Output</span>
          <pre className="bg-muted max-h-60 overflow-auto rounded p-2 text-xs whitespace-pre-wrap">
            {formatValue(run?.output ?? result?.output ?? null)}
          </pre>
        </div>
      )}
    </div>
  );
}
